import { useEffect, useState } from 'react';
import {
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalBody,
	ModalFooter,
	ModalCloseButton,
	Button,
	SimpleGrid,
	FormControl,
	FormLabel,
	Input,
	Select,
	Divider,
	Text,
	Spinner,
	Center,
	useToast,
	useColorModeValue,
} from '@chakra-ui/react';
import {
	ApiProductsMasterGet,
	ApiProductsMasterPatch,
	mapProductsMasterRow,
	toProductsMasterUpdatePayload,
} from 'api/masters/productsList';
import {
	ProductDistribucion,
	ABC_OPTIONS,
	AbcValue,
	ProductMasterRow,
	ProductMetodoPago,
	ProductProveedor,
	ProductTransporte,
} from './types';

type Props = {
	product: ProductMasterRow | null;
	isOpen: boolean;
	onClose: () => void;
	onSave: (updated: ProductMasterRow) => void;
};

function emptyProveedor(): ProductProveedor {
	return {
		codigo: '',
		codigoErp: '',
		descripcion: '',
		descripcionProductoProveedor: '',
		email: '',
		nombreContacto: '',
		pais: '',
	};
}

function emptyDistribucion(): ProductDistribucion {
	return {
		costoFobUnidad: '',
		costoFobBulto: '',
		unidadDespacho: '',
		conteoDespacho: '',
		codigoAlmacen: '',
	};
}

function emptyTransporte(): ProductTransporte {
	return {
		cpe: '',
		codigoArancelario: '',
		moq: '',
		tiempoPromedioProduccion: '',
		puerto1: '',
		puerto2: '',
		puerto3: '',
	};
}

function emptyMetodoPago(): ProductMetodoPago {
	return { advance: '', balance: '' };
}

function isZeroPlaceholder(value: string): boolean {
	const trimmed = (value ?? '').trim();
	if (!trimmed) return false;
	return /^0+(\.0+)?$/.test(trimmed);
}

function attributePlaceholder(value: string): string {
	const trimmed = (value ?? '').trim();
	if (!trimmed || isZeroPlaceholder(trimmed)) return '';
	return trimmed;
}

function resolvedValue(edited: string, fallback: string): string {
	return edited.trim() || fallback.trim();
}

function mergeProveedor(
	draft: ProductProveedor,
	defaults: ProductProveedor,
): ProductProveedor {
	return {
		codigo: resolvedValue(draft.codigo, defaults.codigo),
		codigoErp: resolvedValue(draft.codigoErp, defaults.codigoErp),
		descripcion: resolvedValue(draft.descripcion, defaults.descripcion),
		descripcionProductoProveedor: resolvedValue(
			draft.descripcionProductoProveedor,
			defaults.descripcionProductoProveedor,
		),
		email: resolvedValue(draft.email, defaults.email),
		nombreContacto: resolvedValue(draft.nombreContacto, defaults.nombreContacto),
		pais: resolvedValue(draft.pais, defaults.pais),
	};
}

function mergeDistribucion(
	draft: ProductDistribucion,
	defaults: ProductDistribucion,
): ProductDistribucion {
	return {
		costoFobUnidad: resolvedValue(draft.costoFobUnidad, defaults.costoFobUnidad),
		costoFobBulto: resolvedValue(draft.costoFobBulto, defaults.costoFobBulto),
		unidadDespacho: resolvedValue(draft.unidadDespacho, defaults.unidadDespacho),
		conteoDespacho: resolvedValue(draft.conteoDespacho, defaults.conteoDespacho),
		codigoAlmacen: resolvedValue(draft.codigoAlmacen, defaults.codigoAlmacen),
	};
}

function mergeTransporte(
	draft: ProductTransporte,
	defaults: ProductTransporte,
): ProductTransporte {
	return {
		cpe: resolvedValue(draft.cpe, defaults.cpe),
		codigoArancelario: resolvedValue(draft.codigoArancelario, defaults.codigoArancelario),
		moq: resolvedValue(draft.moq, defaults.moq),
		tiempoPromedioProduccion: resolvedValue(
			draft.tiempoPromedioProduccion,
			defaults.tiempoPromedioProduccion,
		),
		puerto1: resolvedValue(draft.puerto1, defaults.puerto1),
		puerto2: resolvedValue(draft.puerto2, defaults.puerto2),
		puerto3: resolvedValue(draft.puerto3, defaults.puerto3),
	};
}

function mergeMetodoPago(
	draft: ProductMetodoPago,
	defaults: ProductMetodoPago,
): ProductMetodoPago {
	return {
		advance: resolvedValue(draft.advance, defaults.advance),
		balance: resolvedValue(draft.balance, defaults.balance),
	};
}

type AttributeInputProps = {
	value: string;
	placeholderSource: string;
	onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
	inputSx: Record<string, unknown>;
};

function AttributeInput({
	value,
	placeholderSource,
	onChange,
	inputSx,
}: AttributeInputProps) {
	return (
		<Input
			value={value}
			placeholder={attributePlaceholder(placeholderSource)}
			onChange={onChange}
			sx={inputSx}
		/>
	);
}

export default function ProductEditModal({ product, isOpen, onClose, onSave }: Props) {
	const toast = useToast();
	const [defaults, setDefaults] = useState<ProductMasterRow | null>(null);
	const [proveedor, setProveedor] = useState<ProductProveedor>(emptyProveedor);
	const [distribucion, setDistribucion] = useState<ProductDistribucion>(emptyDistribucion);
	const [transporte, setTransporte] = useState<ProductTransporte>(emptyTransporte);
	const [metodoPago, setMetodoPago] = useState<ProductMetodoPago>(emptyMetodoPago);
	const [abc, setAbc] = useState<AbcValue>('');
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const inputTextColor = useColorModeValue('secondaryGray.900', 'white');
	const placeholderColor = useColorModeValue('secondaryGray.400', 'whiteAlpha.500');
	const inputBorderColor = useColorModeValue('secondaryGray.200', 'whiteAlpha.200');
	const inputSx = {
		color: inputTextColor,
		bg: 'transparent',
		borderColor: inputBorderColor,
		_placeholder: { color: placeholderColor },
		_hover: { bg: 'transparent', borderColor: inputBorderColor },
		_focus: { bg: 'transparent', borderColor: 'brand.500' },
	} as const;

	useEffect(() => {
		if (!product || !isOpen) return;

		let cancelled = false;
		setLoading(true);
		setDefaults(null);
		setProveedor(emptyProveedor());
		setDistribucion(emptyDistribucion());
		setTransporte(emptyTransporte());
		setMetodoPago(emptyMetodoPago());
		setAbc(product.abc ?? '');

		ApiProductsMasterGet(product.codigo)
			.then((response) => {
				if (cancelled) return;
				const row = mapProductsMasterRow(response.data);
				setDefaults(row);
				setAbc(row.abc ?? '');
			})
			.catch(() => {
				if (cancelled) return;
				setDefaults({
					...product,
					proveedor: { ...product.proveedor },
					distribucion: { ...product.distribucion },
					transporte: { ...product.transporte },
					metodoPago: { ...product.metodoPago },
				});
				toast({
					title: 'No se pudieron cargar los atributos',
					description: 'Se usan los datos del listado como referencia.',
					status: 'warning',
					duration: 4000,
				});
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});

		return () => {
			cancelled = true;
		};
	}, [product, isOpen, toast]);

	if (!product) return null;

	const handleSave = async () => {
		if (!defaults) return;

		const merged: ProductMasterRow = {
			...product,
			proveedor: mergeProveedor(proveedor, defaults.proveedor),
			distribucion: mergeDistribucion(distribucion, defaults.distribucion),
			transporte: mergeTransporte(transporte, defaults.transporte),
			metodoPago: mergeMetodoPago(metodoPago, defaults.metodoPago),
			abc,
		};

		setSaving(true);
		try {
			const response = await ApiProductsMasterPatch(
				product.codigo,
				toProductsMasterUpdatePayload(merged),
			);
			onSave(mapProductsMasterRow(response.data));
			onClose();
		} catch {
			toast({
				title: 'No se pudieron guardar los cambios',
				status: 'error',
				duration: 4000,
			});
		} finally {
			setSaving(false);
		}
	};

	const attr = defaults;

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
			<ModalOverlay />
			<ModalContent maxH="90vh">
				<ModalHeader>Editar producto — {product.codigo}</ModalHeader>
				<ModalCloseButton />
				<ModalBody>
					{loading || !attr ? (
						<Center py={12}>
							<Spinner size="lg" />
						</Center>
					) : (
						<>
							<Text fontSize="sm" color="secondaryGray.600" mb={4}>
								Los valores actuales se muestran como referencia en cada campo. Escribe
								solo lo que quieras cambiar.
							</Text>
							<Text fontWeight="600" mb={3}>
								Proveedor
							</Text>
							<SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={6}>
								<FormControl>
									<FormLabel>Código</FormLabel>
									<AttributeInput
										value={proveedor.codigo}
										placeholderSource={attr.proveedor.codigo}
										inputSx={inputSx}
										onChange={(e) =>
											setProveedor({ ...proveedor, codigo: e.target.value })
										}
									/>
								</FormControl>
								<FormControl>
									<FormLabel>Código ERP</FormLabel>
									<AttributeInput
										value={proveedor.codigoErp}
										placeholderSource={attr.proveedor.codigoErp}
										inputSx={inputSx}
										onChange={(e) =>
											setProveedor({ ...proveedor, codigoErp: e.target.value })
										}
									/>
									<Text fontSize="xs" opacity={0.7} mt={1}>
										El que viaja a Profit al cargar la orden de compra. Distinto
										de «Código», que suele traer la referencia del proveedor.
									</Text>
								</FormControl>
								<FormControl>
									<FormLabel>Descripción</FormLabel>
									<AttributeInput
										value={proveedor.descripcion}
										placeholderSource={attr.proveedor.descripcion}
										inputSx={inputSx}
										onChange={(e) =>
											setProveedor({ ...proveedor, descripcion: e.target.value })
										}
									/>
								</FormControl>
								<FormControl gridColumn={{ md: '1 / -1' }}>
									<FormLabel>Descripción del producto del proveedor</FormLabel>
									<AttributeInput
										value={proveedor.descripcionProductoProveedor}
										placeholderSource={attr.proveedor.descripcionProductoProveedor}
										inputSx={inputSx}
										onChange={(e) =>
											setProveedor({
												...proveedor,
												descripcionProductoProveedor: e.target.value,
											})
										}
									/>
								</FormControl>
								<FormControl>
									<FormLabel>Nombre contacto email</FormLabel>
									<AttributeInput
										value={proveedor.nombreContacto}
										placeholderSource={attr.proveedor.nombreContacto}
										inputSx={inputSx}
										onChange={(e) =>
											setProveedor({
												...proveedor,
												nombreContacto: e.target.value,
											})
										}
									/>
								</FormControl>
								<FormControl>
									<FormLabel>Email contacto proveedor</FormLabel>
									<AttributeInput
										value={proveedor.email}
										placeholderSource={attr.proveedor.email}
										inputSx={inputSx}
										onChange={(e) =>
											setProveedor({ ...proveedor, email: e.target.value })
										}
									/>
								</FormControl>
								<FormControl>
									<FormLabel>País</FormLabel>
									<AttributeInput
										value={proveedor.pais}
										placeholderSource={attr.proveedor.pais}
										inputSx={inputSx}
										onChange={(e) =>
											setProveedor({ ...proveedor, pais: e.target.value })
										}
									/>
								</FormControl>
							</SimpleGrid>
							<Divider my={4} />
							<Text fontWeight="600" mb={3}>
								Distribución
							</Text>
							<SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={6}>
								<FormControl>
									<FormLabel>Costo FOB x unidad</FormLabel>
									<AttributeInput
										value={distribucion.costoFobUnidad}
										placeholderSource={attr.distribucion.costoFobUnidad}
										inputSx={inputSx}
										onChange={(e) =>
											setDistribucion({
												...distribucion,
												costoFobUnidad: e.target.value,
											})
										}
									/>
								</FormControl>
								<FormControl>
									<FormLabel>Costo FOB x bulto</FormLabel>
									<AttributeInput
										value={distribucion.costoFobBulto}
										placeholderSource={attr.distribucion.costoFobBulto}
										inputSx={inputSx}
										onChange={(e) =>
											setDistribucion({
												...distribucion,
												costoFobBulto: e.target.value,
											})
										}
									/>
								</FormControl>
								<FormControl>
									<FormLabel>Unidad de despacho</FormLabel>
									<AttributeInput
										value={distribucion.unidadDespacho}
										placeholderSource={attr.distribucion.unidadDespacho}
										inputSx={inputSx}
										onChange={(e) =>
											setDistribucion({
												...distribucion,
												unidadDespacho: e.target.value,
											})
										}
									/>
								</FormControl>
								<FormControl>
									<FormLabel>Conteo despacho</FormLabel>
									<AttributeInput
										value={distribucion.conteoDespacho}
										placeholderSource={attr.distribucion.conteoDespacho}
										inputSx={inputSx}
										onChange={(e) =>
											setDistribucion({
												...distribucion,
												conteoDespacho: e.target.value,
											})
										}
									/>
								</FormControl>
								<FormControl>
									<FormLabel>Código almacén</FormLabel>
									<AttributeInput
										value={distribucion.codigoAlmacen}
										placeholderSource={attr.distribucion.codigoAlmacen}
										inputSx={inputSx}
										onChange={(e) =>
											setDistribucion({
												...distribucion,
												codigoAlmacen: e.target.value,
											})
										}
									/>
									<Text fontSize="xs" opacity={0.7} mt={1}>
										Almacén destino en el ERP, usado al cargar la orden de
										compra en Profit.
									</Text>
								</FormControl>
							</SimpleGrid>
							<Divider my={4} />
							<Text fontWeight="600" mb={3}>
								Transporte
							</Text>
							<SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={6}>
								<FormControl>
									<FormLabel>CPE</FormLabel>
									<AttributeInput
										value={transporte.cpe}
										placeholderSource={attr.transporte.cpe}
										inputSx={inputSx}
										onChange={(e) =>
											setTransporte({ ...transporte, cpe: e.target.value })
										}
									/>
								</FormControl>
								<FormControl>
									<FormLabel>Código arancelario</FormLabel>
									<AttributeInput
										value={transporte.codigoArancelario}
										placeholderSource={attr.transporte.codigoArancelario}
										inputSx={inputSx}
										onChange={(e) =>
											setTransporte({
												...transporte,
												codigoArancelario: e.target.value,
											})
										}
									/>
								</FormControl>
								<FormControl>
									<FormLabel>MOQ</FormLabel>
									<AttributeInput
										value={transporte.moq}
										placeholderSource={attr.transporte.moq}
										inputSx={inputSx}
										onChange={(e) =>
											setTransporte({ ...transporte, moq: e.target.value })
										}
									/>
								</FormControl>
								<FormControl>
									<FormLabel>Tiempo promedio de producción</FormLabel>
									<AttributeInput
										value={transporte.tiempoPromedioProduccion}
										placeholderSource={attr.transporte.tiempoPromedioProduccion}
										inputSx={inputSx}
										onChange={(e) =>
											setTransporte({
												...transporte,
												tiempoPromedioProduccion: e.target.value,
											})
										}
									/>
								</FormControl>
								<FormControl>
									<FormLabel>Puerto 1</FormLabel>
									<AttributeInput
										value={transporte.puerto1}
										placeholderSource={attr.transporte.puerto1}
										inputSx={inputSx}
										onChange={(e) =>
											setTransporte({ ...transporte, puerto1: e.target.value })
										}
									/>
								</FormControl>
								<FormControl>
									<FormLabel>Puerto 2</FormLabel>
									<AttributeInput
										value={transporte.puerto2}
										placeholderSource={attr.transporte.puerto2}
										inputSx={inputSx}
										onChange={(e) =>
											setTransporte({ ...transporte, puerto2: e.target.value })
										}
									/>
								</FormControl>
								<FormControl>
									<FormLabel>Puerto 3</FormLabel>
									<AttributeInput
										value={transporte.puerto3}
										placeholderSource={attr.transporte.puerto3}
										inputSx={inputSx}
										onChange={(e) =>
											setTransporte({ ...transporte, puerto3: e.target.value })
										}
									/>
								</FormControl>
							</SimpleGrid>
							<Divider my={4} />
							<Text fontWeight="600" mb={3}>
								Método de pago
							</Text>
							<SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
								<FormControl>
									<FormLabel>Advance</FormLabel>
									<AttributeInput
										value={metodoPago.advance}
										placeholderSource={attr.metodoPago.advance}
										inputSx={inputSx}
										onChange={(e) =>
											setMetodoPago({ ...metodoPago, advance: e.target.value })
										}
									/>
								</FormControl>
								<FormControl>
									<FormLabel>Balance</FormLabel>
									<AttributeInput
										value={metodoPago.balance}
										placeholderSource={attr.metodoPago.balance}
										inputSx={inputSx}
										onChange={(e) =>
											setMetodoPago({ ...metodoPago, balance: e.target.value })
										}
									/>
								</FormControl>
							</SimpleGrid>
							<Divider my={4} />
							<Text fontWeight="600" mb={3}>
								Clasificación
							</Text>
							<SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={6}>
								<FormControl>
									<FormLabel>ABC</FormLabel>
									<Select
										value={abc}
										placeholder="Sin definir"
										sx={inputSx}
										onChange={(e) => setAbc(e.target.value as AbcValue)}>
										{ABC_OPTIONS.map((opt) => (
											<option key={opt} value={opt}>
												{opt}
											</option>
										))}
									</Select>
								</FormControl>
							</SimpleGrid>
						</>
					)}
				</ModalBody>
				<ModalFooter>
					<Button variant="ghost" mr={3} onClick={onClose} isDisabled={saving}>
						Cancelar
					</Button>
					<Button
						colorScheme="brand"
						onClick={handleSave}
						isLoading={saving}
						isDisabled={loading || !defaults}>
						Guardar
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
}
